import React from 'react';
import { motion } from 'framer-motion';
import { ChevronRight, Zap, Archive, XCircle } from 'lucide-react';
import { FiPackage } from 'react-icons/fi';
import { TbCircleCheck } from 'react-icons/tb';
import { PiUserCircleDashed } from 'react-icons/pi';
import { useAiCommandPalette } from '../context/AiCommandPaletteContext';
import AiOption from './AiOption';
import { STATUS_COLORS, getReleaseStatusColor } from '../../../lib/colorsMap';
import { getIconComponent } from '../../../lib/statusIconsMap';

// Helper to determine release status from release data
const getReleaseStatus = (release) => {
  if (!release) return 'upcoming';
  if (release.actualReleaseDate) return 'shipped';
  
  const now = new Date();
  const start = release.startDate ? new Date(release.startDate) : null;
  const target = release.targetDate ? new Date(release.targetDate) : null;
  
  if (target && now > target) return 'overdue';
  if (start && now >= start) return 'active';
  return 'upcoming';
};

// Helper to check if a task was completed in a past week
const isCompletedInPastWeek = (task) => {
  if (!task.completedAt) return false;
  
  const now = new Date();
  const currentWeekStart = new Date(now);
  currentWeekStart.setDate(now.getDate() - now.getDay());
  currentWeekStart.setHours(0, 0, 0, 0);
  
  const completedDate = new Date(task.completedAt);
  return completedDate < currentWeekStart;
};

const SearchResults = () => {
  const {
    searchResults,
    selectedIndex,
    setSelectedIndex,
    inputValue,
    handleNavigateToTask,
    isAskMode,
    conversationHistory
  } = useAiCommandPalette();
  
  // Don't show search results when in ask mode with a conversation
  if (isAskMode && conversationHistory.length > 0) return null;
  if (!inputValue.trim()) return null;

  return (
    <div 
      className={`${inputValue.trim() ? 'p-4 space-y-2' : ''}`}
      onMouseLeave={() => setSelectedIndex(-1)}
    >      
      {searchResults.map((result, index) => {
        // Get status icon and color from statusConfig
        const StatusIconComponent = result.statusConfig?.icon 
          ? getIconComponent(result.statusConfig.icon) 
          : null;
        const statusColorClass = result.statusConfig?.color && STATUS_COLORS[result.statusConfig.color]?.text 
          ? STATUS_COLORS[result.statusConfig.color].text 
          : 'text-neutral-500';
        const assigneeDotClass = result.assignee?.avatarColor || 'bg-neutral-500';
        
        // Render scope display (matching Tasks.jsx logic)
        const renderScope = () => {
          if (result.closedAt) {
            return (
              <div className="flex items-center gap-1">
                <XCircle className="w-3.5 h-3.5 text-muted-foreground" />
                <span>Closed</span>
              </div>
            );
          }
          
          if (result.release) {
            const releaseStatus = getReleaseStatus(result.release);
            const releaseColorClass = getReleaseStatusColor(releaseStatus).icon;
            return (
              <div className="flex items-center gap-1">
                <FiPackage className={`w-3.5 h-3.5 ${releaseColorClass}`} />
                <span className="truncate max-w-[80px]" title={result.release.name}>
                  {result.release.name}
                </span>
              </div>
            );
          }
          
          if (result.scope === 'backlog') {
            return (
              <div className="flex items-center gap-1">
                <Archive className="w-3.5 h-3.5 text-neutral-600 dark:text-neutral-400" />
                <span>Backlog</span>
              </div>
            );
          }
          
          if (isCompletedInPastWeek(result)) {
            return (
              <div className="flex items-center gap-1">
                <TbCircleCheck className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                <span>Previous Week</span>
              </div>
            );
          }
          
          // Default: Active Week
          return (
            <div className="flex items-center gap-1">
              <Zap className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
              <span>Active Week</span>
            </div>
          );
        };
        
        return (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            key={result.id}
            onClick={() => handleNavigateToTask(result)}
            onMouseMove={() => setSelectedIndex(index)}
            className={`cursor-pointer group block rounded-lg px-3 py-2.5 border transition-[border-color,box-shadow] duration-200 ${
              index === selectedIndex
                ? 'bg-gradient-to-r from-purple-50/60 to-violet-50/40 dark:from-purple-500/10 dark:to-violet-500/5 border-purple-300/50 dark:border-purple-400/20 shadow-sm'
                : 'bg-transparent border-transparent hover:bg-gradient-to-r hover:from-purple-50/40 hover:to-violet-50/30 dark:hover:from-purple-500/5 dark:hover:to-violet-500/[0.02] hover:border-purple-200/30 dark:hover:border-purple-400/10'
            }`}
          >
            <div className="flex items-start gap-3">
              <div className="flex-1 min-w-0">
                {/* Summary */}
                <div className="font-medium text-neutral-900 dark:text-white text-sm truncate group-hover:text-neutral-700 dark:group-hover:text-neutral-100 transition-colors">
                  {result.summary}
                </div>
                
                {/* Meta row */}
                <div className="flex items-center mt-1.5 gap-3 text-xs text-neutral-500 dark:text-neutral-400">
                  {/* Task key */}
                  <span className="font-medium text-neutral-600 dark:text-neutral-300">{result.key}</span>
                  
                  {/* Status */}
                  <div className="flex items-center gap-1">
                    {StatusIconComponent ? (
                      <StatusIconComponent className={`w-3.5 h-3.5 ${statusColorClass}`} />
                    ) : (
                      <span className="w-2 h-2 rounded-full bg-neutral-400" />
                    )}
                    <span>{result.statusConfig?.name || result.status}</span>
                  </div>
                  
                  {/* Scope (with release support) */}
                  {renderScope()}
                  
                  {/* Project */}
                  {result.project?.name && (
                    <>
                      <span className="h-1 w-1 bg-neutral-300 dark:bg-neutral-600 rounded-full" />
                      <span className="truncate max-w-[100px]">{result.project.name}</span>
                    </>
                  )}
                  
                  {/* Assignee */}
                  {result.assignee ? (
                    <div className="flex items-center gap-1">
                      <span className={`w-3 h-3 rounded-full ${assigneeDotClass} text-white text-[7px] font-bold flex items-center justify-center`}>
                        {result.assignee.name.charAt(0).toUpperCase()}
                      </span>
                      <span className="truncate max-w-[70px]">{result.assignee.name}</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 text-neutral-400">
                      <PiUserCircleDashed className="w-3.5 h-3.5" />
                      <span>Unassigned</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Chevron */}
              <div className="flex items-center self-center">
                <ChevronRight className={`h-4 w-4 text-neutral-400 transition-opacity ${
                  index === selectedIndex ? 'opacity-100' : 'opacity-0'
                }`} />
              </div>
            </div>
          </motion.div>
        );
      })}

      <AiOption />
    </div>
  );
};

export default SearchResults;
